
import { PatientData, User, MatchResult, TreatmentPlan, TreatmentStep, UserProfileType } from '../types';
import { CORES_CROMOTERAPIA, TEMPOS_APLICACAO, DISCLAIMER_TEXT, TRATAMENTOS_PRATICOS } from '../constants';
import { checkLimits } from './authService';
import { db } from './firebaseConfig';
import { collection, addDoc, doc, updateDoc, increment, query, where, getDocs, orderBy, limit, getDoc } from 'firebase/firestore';

const REGIOES_CORPO = [
  { keywords: ["pé", "calc", "plantar", "dedo", "tornozelo"], regiao: "Extremidades Inferiores (Pés)" },
  { keywords: ["mão", "pulso", "punho", "carpo", "braço", "cotovelo", "ombro"], regiao: "Membros Superiores (Punho/Braço)" },
  { keywords: ["costas", "lombar", "coluna", "cervical"], regiao: "Eixo Vertebral" },
  { keywords: ["barriga", "estomago", "ventre", "abdome"], regiao: "Região Abdominal" },
  { keywords: ["cabeça", "testa", "face"], regiao: "Região Cefálica" },
  { keywords: ["ouvido", "orelha", "auditivo"], regiao: "Região Auricular" },
  { keywords: ["garganta", "pescoço"], regiao: "Região Laríngea" }
];

const gerarPlanoManual = (patient: PatientData): TreatmentPlan => {
  const lowerText = (patient.sintomasTexto || "").toLowerCase();
  const ageData = TEMPOS_APLICACAO.find(t => patient.idade >= t.min && patient.idade <= t.max) || TEMPOS_APLICACAO[2];
  const passos: TreatmentStep[] = [];

  passos.push({
    fase: 'Inicial',
    descricao: 'Limpeza e Abertura',
    regiao: 'Aura e Chakra Coronário',
    detalhes: 'Preparação do campo para recepção energética.',
    ideoplastia: 'Visualize uma luz branca cristalina limpando todo o campo áurico.'
  });

  let regiaoAlvo = "Local da Queixa";
  const regiaoEncontrada = REGIOES_CORPO.find(r => r.keywords.some(k => lowerText.includes(k)));
  if (regiaoEncontrada) regiaoAlvo = regiaoEncontrada.regiao;

  const isInflammation = lowerText.includes("ite") || lowerText.includes("inflama") || lowerText.includes("quente");
  
  passos.push({
    fase: 'Central',
    descricao: 'Tratamento Local',
    regiao: regiaoAlvo,
    cor: isInflammation ? 'Verde' : 'Azul',
    tempo: `${ageData.tempoBase} segundos`,
    detalhes: 'Aplicação focada na harmonização da região afetada.',
    ideoplastia: `Imagine a cor envolvendo especificamente a região: ${regiaoAlvo}.`
  });

  passos.push({
    fase: 'Final',
    descricao: 'Proteção e Selamento',
    regiao: 'Todo o Corpo',
    cor: 'Azul',
    tempo: '30 segundos',
    detalhes: 'Fixação das cores aplicadas e proteção da aura.',
    ideoplastia: 'Envolva o assistido em uma bolha de luz azul suave.'
  });

  return {
    id: Math.random().toString(36).substr(2, 9),
    patientName: patient.nome,
    dateCreated: new Date().toLocaleDateString('pt-BR'),
    resumo: `Protocolo gerado por reconhecimento anatômico (${regiaoAlvo}).`,
    passos: passos,
    frequencia: ageData.frequencia,
    consideracoesGerais: [ageData.consideracoes],
    disclaimer: DISCLAIMER_TEXT,
    generationMethod: 'Manual'
  };
};

export const gerarPlanoTratamento = async (
  patient: PatientData, 
  user: User
): Promise<{ result: MatchResult, updatedUser: User }> => {
  const limitCheck = checkLimits(user);
  if (!limitCheck.allowed) return { result: { error: limitCheck.error }, updatedUser: user };

  let plan: TreatmentPlan | null = null;
  try {
     const aiModule = await import('./aiService');
     if (aiModule && aiModule.generateProtocolWithAI) {
        plan = await aiModule.generateProtocolWithAI(patient);
     }
  } catch (e) {
     console.warn("AI Service indisponível, usando motor manual.");
  }

  if (!plan) plan = gerarPlanoManual(patient);

  try {
    const treatmentDoc = {
        userId: user.id,
        userEmail: user.email,
        patientName: patient.nome.trim(),
        patientNameLower: patient.nome.trim().toLowerCase(),
        createdAt: new Date().toISOString(),
        method: plan.generationMethod || 'Manual',
        plan: plan 
    };

    const docRef = await addDoc(collection(db, 'treatments'), treatmentDoc);
    const planWithId = { ...plan, id: docRef.id };

    const userRef = doc(db, 'users', user.id);
    await updateDoc(userRef, { requestsToday: increment(1) });
    
    return { result: { plan: planWithId }, updatedUser: { ...user, requestsToday: user.requestsToday + 1 } };
  } catch (error: any) {
    return { 
      result: { plan, error: "Protocolo gerado, mas houve uma falha ao salvar no banco." }, 
      updatedUser: user 
    }; 
  }
};

export const getPlanById = async (id: string): Promise<TreatmentPlan | null> => {
  try {
    const docRef = doc(db, 'treatments', id);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      const data = docSnap.data();
      return { ...data.plan, id: docSnap.id };
    }
    return null;
  } catch (e) {
    return null;
  }
};

export const getHistory = async (userId: string, profile: UserProfileType): Promise<TreatmentPlan[]> => {
  try {
    const treatmentsCol = collection(db, 'treatments');
    let q;

    if (profile === UserProfileType.MASTER) {
      q = query(treatmentsCol, orderBy('createdAt', 'desc'), limit(100));
    } else {
      q = query(treatmentsCol, where('userId', '==', userId));
    }
    
    const snapshot = await getDocs(q);
    const plans: TreatmentPlan[] = [];
    
    snapshot.forEach(doc => {
      const data = doc.data();
      if (data.plan) {
        plans.push({
          ...data.plan,
          id: doc.id,
          patientName: data.patientName || data.plan.patientName
        });
      }
    });

    // Ordenação manual para evitar erros de índice composto no Firestore para usuários comuns
    return plans.sort((a, b) => {
        // @ts-ignore
        const dateA = new Date(a.dateCreated).getTime();
        // @ts-ignore
        const dateB = new Date(b.dateCreated).getTime();
        return dateB - dateA;
    });
  } catch (error) {
    return [];
  }
};
